import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, ArrowUpRight, Gauge, Star, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { Img } from "./Img";
import { SERVICES } from "../data/services";
import { BIZ, track, GOOGLE_RATING, GOOGLE_REVIEW_COUNT, GOOGLE_REVIEWS_URL } from "../data/site";

export const FrameFeature = () => (
  <section data-testid="frame-feature" className="relative py-24 lg:py-32 surface-panel border-y border-white/[0.06] overflow-hidden noise-overlay">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <Reveal className="lg:col-span-6">
        <Eyebrow>Our #1 Differentiator</Eyebrow>
        <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.08] text-balance">
          The Computerized <span className="accent-text">Frame Machine</span>
        </h2>
        <p className="mt-6 text-sm sm:text-base text-slate-300 leading-relaxed max-w-xl">
          Most shops straighten structure by experience and eyeball. We do it by laser. Our frame
          bench anchors your vehicle at factory datum points and tracks every reference point in
          real time while controlled hydraulic pressure corrects the damage — then prints the proof.
        </p>
        <p className="mt-4 text-sm sm:text-base text-slate-400 leading-relaxed max-w-xl">
          You receive the before-and-after measurement report with your keys: objective evidence that
          your chassis is within factory tolerance, and the document that protects your vehicle&apos;s
          value at resale.
        </p>
        <Link
          to="/services/frame-straightening"
          data-testid="frame-feature-link"
          className="mt-8 inline-flex items-center gap-2 min-h-11 font-mono text-[11px] tracking-[0.2em] uppercase text-accent hover:text-accentdeep transition-colors"
        >
          Explore frame straightening <ArrowUpRight size={14} />
        </Link>
      </Reveal>
      <Reveal className="lg:col-span-6" delay={0.12}>
        <div className="clip-frame overflow-hidden rounded-sm border border-white/10">
          <Img name="frame-straightening-carrollton-tx" alt="Vehicle frame straightening on the computerized frame machine at Westway Collision Center in Carrollton, TX" className="h-72 sm:h-96 w-full object-cover" sizes="(min-width: 1024px) 50vw, 100vw" />
        </div>
      </Reveal>
    </div>
  </section>
);

export const DmeHighlight = () => (
  <section data-testid="dme-highlight" className="relative py-24 lg:py-32 overflow-hidden">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <div className="relative overflow-hidden rounded-3xl border border-accent/25">
          <Img name="mercedes-g-wagon-westway-collision" alt="Mercedes G-Wagon at Westway Collision Center — performance and DME tuning division in Carrollton, TX" className="absolute inset-0 h-full w-full object-cover" sizes="100vw" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/30" aria-hidden="true" />
          <div className="relative p-8 sm:p-14 lg:p-20 max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-4 py-1.5 font-mono text-[10px] tracking-[0.24em] uppercase text-accent">
              <Gauge size={12} />
              Flagship Performance Division
            </span>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.08] text-balance">
              DME Tuning &amp; Repair for Performance Machines
            </h2>
            <p className="mt-5 text-sm sm:text-base text-slate-300 leading-relaxed">
              Emissions-compliant performance tuning, expert DME/ECU diagnostics and repair, and
              certified collision restoration for BMW M, Porsche, Mercedes-AMG, and the world&apos;s
              finest exotics — all under one roof.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/dme-tuning-dallas"
                data-testid="dme-highlight-link"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-accentdeep px-7 py-3.5 font-display font-bold text-sm text-white transition-all duration-300 hover:bg-accenthover hover:-translate-y-0.5"
              >
                Explore Performance &amp; DME <ArrowUpRight size={14} />
              </Link>
              <a
                href={BIZ.phoneHref}
                onClick={() => track("click_to_call", { location: "dme_highlight" })}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 bg-white/[0.05] px-7 py-3.5 font-display font-semibold text-sm text-white transition-all duration-300 hover:border-accent/60 hover:text-accent"
              >
                <Phone size={15} />
                {BIZ.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const WORK_PREVIEW = [
  ["corvette-repair-after-carrollton", "Corvette finished after collision repair at Westway Collision Center in Carrollton, TX"],
  ["mercedes-g-wagon-westway-collision", "Mercedes G-Wagon serviced at Westway Collision Center in Carrollton, TX"],
  ["lamborghini-huracan-performance-tuning", "Lamborghini Huracan restored at Westway Collision Center in Carrollton, TX"],
  ["rolls-royce-spectre-service-bay", "Rolls-Royce Spectre in the service bay at Westway Collision Center in Carrollton, TX"],
  ["customer-chevrolet-tahoe-westway", "Chevrolet Tahoe delivered back to its owner at Westway Collision Center in Carrollton, TX"],
];

export const HomeWorkGallery = () => (
  <section data-testid="home-work-gallery" className="py-20 lg:py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
        <div>
          <Eyebrow>Real Cars, Real Shop</Eyebrow>
          <h2 className="mt-4 font-display text-2xl sm:text-4xl font-bold tracking-tight text-white">
            Our Work Speaks in Horsepower
          </h2>
        </div>
        <Link to="/our-work" data-testid="home-gallery-link" className="inline-flex items-center gap-2 min-h-11 font-mono text-[11px] tracking-[0.2em] uppercase text-accent hover:text-accentdeep transition-colors shrink-0">
          Full gallery <ArrowRight size={13} />
        </Link>
      </Reveal>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {WORK_PREVIEW.map(([name, alt], i) => (
          <Reveal key={name} delay={i * 0.05}>
            <Link to="/our-work" data-testid={`home-work-${i}`} className="group block overflow-hidden rounded-2xl border border-white/[0.08] transition-all duration-500 hover:border-accent/30 hover:-translate-y-1">
              <Img name={name} alt={alt} className="h-40 sm:h-48 w-full object-cover" sizes="(min-width: 1024px) 20vw, 50vw" />
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const REVIEWS = [
  {
    name: "Kalyan M Penukula",
    text: "Front office folks are very helpful, and especially Carolina had answered all my questions and I felt confident moving forward with them.",
  },
  {
    name: "Justin B",
    text: "Great job on fixing my car!! It looks brand new, thanks!!",
  },
  {
    name: "Murad Muazzam",
    text: "The service was unparalleled. The moment I arrived, Baha was so helpful and knowledgeable about the needs of my vehicle.",
  },
  {
    name: "Sudeep Vista",
    text: "Friendly members and good service.",
  },
];

export const ReviewsPlaceholder = () => (
  <section data-testid="reviews-section" className="py-20 lg:py-24 surface-panel border-y border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="text-center flex flex-col items-center mb-10">
        <Eyebrow>Verified Google Reviews</Eyebrow>
        <h2 className="mt-4 font-display text-2xl sm:text-4xl font-bold tracking-tight text-white">
          What DFW Owners Say
        </h2>
        <p data-testid="google-rating-summary" className="mt-4 inline-flex items-center gap-3 rounded-full border border-accent/25 bg-accent/[0.06] px-5 py-2.5">
          <span className="flex gap-0.5 text-accent" aria-hidden="true">
            {[...Array(5)].map((_, j) => <Star key={j} size={14} fill="currentColor" />)}
          </span>
          <span className="font-display text-sm font-bold text-white">{GOOGLE_RATING} stars</span>
          <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400">{GOOGLE_REVIEW_COUNT} Google reviews</span>
        </p>
      </Reveal>
      <Reveal className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto mb-10">
        {[
          ["happy-customer-westway-collision", "Happy customer taking delivery at Westway Collision Center in Carrollton, TX"],
          ["customer-chevrolet-tahoe-westway", "Customer with a finished Chevrolet Tahoe at Westway Collision Center in Carrollton, TX"],
        ].map(([name, alt]) => (
          <div key={name} className="clip-frame overflow-hidden rounded-sm border border-white/10">
            <Img name={name} alt={alt} className="h-52 sm:h-60 w-full object-cover" sizes="(min-width: 640px) 50vw, 100vw" />
          </div>
        ))}
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {REVIEWS.map((r, i) => (
          <Reveal key={r.name} delay={i * 0.06}>
            <figure data-testid={`review-card-${i}`} className="h-full rounded-2xl border border-white/[0.08] bg-surface p-6 transition-colors duration-500 hover:border-accent/30">
              <div className="flex gap-1 text-accent" aria-label={`5 star review by ${r.name}`}>
                {[...Array(5)].map((_, j) => <Star key={j} size={14} fill="currentColor" />)}
              </div>
              <blockquote className="mt-4 text-sm text-slate-300 leading-relaxed">
                &ldquo;{r.text}&rdquo;
              </blockquote>
              <figcaption className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-display text-sm font-semibold text-white">{r.name}</span>
                <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-500">Google review</span>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-10 text-center">
        <a
          href={GOOGLE_REVIEWS_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="read-all-reviews-btn"
          className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-7 py-3.5 font-display font-semibold text-sm text-accent transition-all duration-300 hover:bg-accenthover hover:text-white"
        >
          Read all Google reviews <ArrowUpRight size={14} />
        </a>
      </Reveal>
    </div>
  </section>
);

export const ServicesDirectory = () => (
  <section data-testid="services-directory" className="py-20 lg:py-24 border-t border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
        <div>
          <Eyebrow>Full Directory</Eyebrow>
          <h2 className="mt-4 font-display text-2xl sm:text-4xl font-bold tracking-tight text-white">
            All 15 Services, One Standard
          </h2>
        </div>
        <Link to="/services" data-testid="directory-all-link" className="inline-flex items-center gap-2 min-h-11 font-mono text-[11px] tracking-[0.2em] uppercase text-accent hover:text-accentdeep transition-colors shrink-0">
          Services hub <ArrowUpRight size={13} />
        </Link>
      </Reveal>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {SERVICES.map((s) => (
          <Reveal key={s.slug}>
            <Link
              to={`/services/${s.slug}`}
              data-testid={`directory-${s.slug}`}
              className="group flex h-full flex-col justify-between gap-6 rounded-2xl border border-white/[0.08] bg-surface p-5 transition-all duration-500 hover:border-accent/30 hover:-translate-y-1"
            >
              <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-accent/70">{s.badge}</span>
              <span className="flex items-center justify-between gap-2">
                <span className={`font-display text-sm sm:text-base font-semibold group-hover:text-accent transition-colors ${s.featured ? "text-accent" : "text-slate-100"}`}>
                  {s.menuName}
                </span>
                <ArrowUpRight size={14} className="shrink-0 text-mute group-hover:text-accent transition-colors" aria-hidden="true" />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const HomeCta = () => (
  <section data-testid="home-cta" className="py-24 lg:py-28">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="text-center flex flex-col items-center">
        <Eyebrow>Ready When You Are</Eyebrow>
        <h2 className="mt-5 font-display text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.06] max-w-4xl text-balance">
          Restore It To <span className="accent-text">Factory Condition</span>
        </h2>
        <p className="mt-6 max-w-xl text-sm sm:text-base text-slate-400 leading-relaxed">
          One call. We handle the estimate, the insurance, the rental, and the restoration.
          Your only job is dropping off the keys.
        </p>
        <a
          href={BIZ.phoneHref}
          data-testid="contact-phone-link"
          onClick={() => track("click_to_call", { location: "home_cta" })}
          className="mt-10 inline-flex items-center gap-3 rounded-full bg-accentdeep px-9 py-4 font-display font-bold text-base sm:text-lg text-white transition-colors duration-300 hover:bg-accenthover"
        >
          <Phone size={19} strokeWidth={2.2} />
          {BIZ.phoneDisplay}
        </a>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-6">
          <a href={BIZ.mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-address-link" className="group rounded-2xl border border-white/[0.08] bg-surface/60 p-7 transition-all duration-500 hover:border-accent/30 hover:-translate-y-1">
            <MapPin size={22} className="text-accent" strokeWidth={1.8} aria-hidden="true" />
            <h3 className="mt-4 font-mono text-[10px] tracking-[0.28em] uppercase text-mute">Visit the Facility</h3>
            <p className="mt-2 text-sm sm:text-base text-slate-200 leading-relaxed">{BIZ.street}<br />{BIZ.city}, {BIZ.state} {BIZ.zip}</p>
            <span className="mt-4 inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-accent/80 group-hover:text-accent transition-colors">
              Get directions <ArrowUpRight size={12} />
            </span>
          </a>
          <a href={BIZ.phoneHref} data-testid="contact-call-card" onClick={() => track("click_to_call", { location: "home_card" })} className="group rounded-2xl border border-white/[0.08] bg-surface/60 p-7 transition-all duration-500 hover:border-accent/30 hover:-translate-y-1">
            <Phone size={22} className="text-accent" strokeWidth={1.8} aria-hidden="true" />
            <h3 className="mt-4 font-mono text-[10px] tracking-[0.28em] uppercase text-mute">Direct Line</h3>
            <p className="mt-2 text-sm sm:text-base text-slate-200">{BIZ.phoneDisplay}</p>
            <span className="mt-4 inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-accent/80 group-hover:text-accent transition-colors">
              Tap to call <ArrowUpRight size={12} />
            </span>
          </a>
          <div data-testid="contact-hours-card" className="rounded-2xl border border-white/[0.08] bg-surface/60 p-7">
            <Clock size={22} className="text-accent" strokeWidth={1.8} aria-hidden="true" />
            <h3 className="mt-4 font-mono text-[10px] tracking-[0.28em] uppercase text-mute">Shop Hours</h3>
            <ul className="mt-2 space-y-1.5 text-sm sm:text-base text-slate-200">
              <li className="flex justify-between gap-4"><span>Mon – Fri</span><span className="text-slate-400">7:30 AM – 6:00 PM</span></li>
              <li className="flex justify-between gap-4"><span>Saturday</span><span className="text-slate-400">8:00 AM – 5:00 PM</span></li>
              <li className="flex justify-between gap-4"><span>Sunday</span><span className="text-slate-400">Closed</span></li>
            </ul>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
